import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, finalize, retry } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ApiError } from '@core/models/api/api-models';

/**
 * Base API service with generic HTTP methods and error handling
 * All feature-specific services should extend this class
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  protected http = inject(HttpClient);
  protected baseUrl = environment.apiUrl;

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor() {
    console.log('🌐 ApiService initialized with baseUrl:', this.baseUrl);
  }

  /**
   * Generic GET request
   */
  protected get<T>(endpoint: string, params?: HttpParams): Observable<T> {
    this.setLoading(true);
    const fullUrl = `${this.baseUrl}/${endpoint}`;
    console.log('🌐 API GET Request:', { endpoint, fullUrl, params: params?.toString() });
    return this.http.get<T>(fullUrl, { params })
      .pipe(
        retry({ count: 2, delay: 1000 }),
        catchError(this.handleError),
        finalize(() => this.setLoading(false))
      );
  }

  /**
   * Generic POST request
   */
  protected post<T>(endpoint: string, body: any): Observable<T> {
    this.setLoading(true);
    return this.http.post<T>(`${this.baseUrl}/${endpoint}`, body)
      .pipe(
        catchError(this.handleError),
        finalize(() => this.setLoading(false))
      );
  }

  /**
   * Generic PUT request
   */
  protected put<T>(endpoint: string, body: any): Observable<T> {
    this.setLoading(true);
    return this.http.put<T>(`${this.baseUrl}/${endpoint}`, body)
      .pipe(
        catchError(this.handleError),
        finalize(() => this.setLoading(false))
      );
  }

  /**
   * Generic DELETE request
   */
  protected delete<T>(endpoint: string): Observable<T> {
    this.setLoading(true);
    return this.http.delete<T>(`${this.baseUrl}/${endpoint}`)
      .pipe(
        catchError(this.handleError),
        finalize(() => this.setLoading(false))
      );
  }

  /**
   * Upload file (multipart/form-data)
   */
  protected uploadFile<T>(endpoint: string, file: File, fieldName: string = 'file'): Observable<T> {
    this.setLoading(true);
    const formData = new FormData();
    formData.append(fieldName, file, file.name);

    return this.http.post<T>(`${this.baseUrl}/${endpoint}`, formData)
      .pipe(
        catchError(this.handleError),
        finalize(() => this.setLoading(false))
      );
  }

  /**
   * Post with pre-built FormData (e.g. file + extra fields)
   */
  protected postFormData<T>(endpoint: string, formData: FormData): Observable<T> {
    this.setLoading(true);
    return this.http.post<T>(`${this.baseUrl}/${endpoint}`, formData)
      .pipe(
        catchError(this.handleError),
        finalize(() => this.setLoading(false))
      );
  }

  /**
   * Build query parameters from object
   */
  protected buildParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item.toString());
          });
        } else {
          httpParams = httpParams.set(key, value.toString());
        }
      }
    });

    return httpParams;
  }

  /**
   * Set loading state
   */
  private setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  /**
   * Global error handler
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: ApiError = {
      error: 'An unexpected error occurred',
      timestamp: new Date().toISOString()
    };

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = {
        error: 'Network error',
        details: error.error.message,
        timestamp: new Date().toISOString()
      };
    } else {
      // Backend returned an error response
      if (error.error && typeof error.error === 'object') {
        errorMessage = error.error as ApiError;
      } else {
        errorMessage = {
          error: `Server Error: ${error.status}`,
          details: error.message,
          timestamp: new Date().toISOString()
        };
      }
    }

    console.error('API Error:', errorMessage);
    return throwError(() => errorMessage);
  }
}
